/**
 * 採点の重み（品目詳細）。
 *
 * 作業評価の総合点は、カテゴリ（重要ポイント・食材・火加減・盛り付け）ごとの充足率を
 * 重み付きで平均して出す（src/core/compare.ts の computeScore）。品目によって大事な所が
 * 違う（盛り付けが売りの料理、調味の順番が肝の料理など）ので、品目ごとに変えられるようにする。
 *
 * 書き込みは Callable(setScoreWeights)を通す。料理名の変更と同じく、品目の更新と
 * 台帳(audit_logs)への追記を同時に行うため。既に付いた点数は変わらない。
 */
import { useEffect, useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "../firebase";
import { useI18n } from "../lib/i18n";
import { CATEGORY_LABEL, type Category } from "../lib/evaluation";
import {
  checkWeights,
  DEFAULT_SCORE_WEIGHTS,
  formatWeights,
  isDefaultWeights,
  WEIGHT_ORDER,
  weightsOf,
  type ScoreWeights as Weights,
} from "../lib/score-weights";
import type { DishDoc } from "../lib/types";

const functions = getFunctions(app, "asia-northeast1");
const setScoreWeights = httpsCallable<
  { dishId: string; weights: Weights | null },
  { ok: boolean }
>(functions, "setScoreWeights");

function toDraft(w: Weights): Record<Category, string> {
  return Object.fromEntries(WEIGHT_ORDER.map((c) => [c, String(w[c])])) as Record<Category, string>;
}

export function ScoreWeights({
  dishId,
  dish,
  canEdit,
}: {
  dishId: string;
  dish: DishDoc;
  canEdit: boolean;
}) {
  const { t } = useI18n();
  const current = weightsOf(dish);
  const currentKey = WEIGHT_ORDER.map((c) => current[c]).join(",");
  const [draft, setDraft] = useState<Record<Category, string>>(() => toDraft(current));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // 他の人が変えたとき(onSnapshot で品目文書が流れてくる)は入力欄も追従させる
  useEffect(() => {
    setDraft(toDraft(weightsOf(dish)));
  }, [currentKey]); // 値が変わったときだけ(dish は onSnapshot のたびに別オブジェクトになる)

  const check = checkWeights(draft);
  const dirty = WEIGHT_ORDER.some((c) => draft[c].trim() !== String(current[c]));

  async function save(weights: Weights | null) {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await setScoreWeights({ dishId, weights });
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card score-weights">
      <h2>{t("採点の重み")}</h2>
      <p className="muted small">
        {t("作業評価の総合点は、カテゴリごとの「できている」割合をこの重みで平均して出します。品目ごとに変えられます。変更は次の評価から使われ、既に付いた点数は変わりません。")}
      </p>
      {canEdit ? (
        <>
          <table className="weights">
            <tbody>
              {WEIGHT_ORDER.map((c) => (
                <tr key={c}>
                  <th scope="row">
                    <label htmlFor={`w-${c}`}>{t(CATEGORY_LABEL[c])}</label>
                  </th>
                  <td>
                    <input
                      id={`w-${c}`}
                      type="number"
                      inputMode="numeric"
                      min={0}
                      max={100}
                      step={1}
                      value={draft[c]}
                      disabled={busy}
                      onChange={(e) => {
                        setSaved(false);
                        setDraft((d) => ({ ...d, [c]: e.target.value }));
                      }}
                    />{" "}
                    %
                    <span className="muted small"> {t("既定 {n}%", { n: DEFAULT_SCORE_WEIGHTS[c] })}</span>
                  </td>
                </tr>
              ))}
              <tr>
                <th scope="row">{t("合計")}</th>
                <td>{check.total === null ? "—" : `${check.total}%`}</td>
              </tr>
            </tbody>
          </table>
          {!check.ok && dirty && <p className="error">{t(check.error, check.params)}</p>}
          {error && <p className="error">{error}</p>}
          {saved && !dirty && <p className="muted small">{t("採点の重みを保存しました。")}</p>}
          <button disabled={busy || !dirty || !check.ok} onClick={() => check.ok && void save(check.weights)}>
            {busy ? t("保存中…") : t("重みを保存する")}
          </button>{" "}
          <button
            className="link"
            disabled={busy || isDefaultWeights(current)}
            onClick={() => void save(null)}
          >
            {t("既定に戻す")}
          </button>
        </>
      ) : (
        <p>{formatWeights(current, t)}</p>
      )}
    </div>
  );
}
